import { ChangeEvent, useEffect, useMemo, useState, useRef } from 'react';
import { useFormik } from 'formik';
import Modal from '../../BaseComponents/Modal';
import TextInput from '../../BaseComponents/TextInput';
import Button from '../../BaseComponents/Button';
import './MembersModal.scss';
import DatePicker from '../../BaseComponents/DatePicker';
import {
  createMember,
  getMember,
  updateMember,
  resendPaymentLink
} from '../../../api/members';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { emailRegexp, genders } from '../../../utils/constants';
import PhoneInputComp from '../../BaseComponents/PhoneInput';
import Loader from '../../BaseComponents/Loader';
import {
  GetMemberResponse,
  UserGenderEnum
} from '../../../types/apiGeneratedTypes';
import { setMemberIdToEdit } from '../../../store/membersSlice';
import { toast } from 'react-toastify';
import Select from '../../BaseComponents/Select';
import countryList from 'react-select-country-list';
import { CustomInput } from '../../location/CustomInput';
 

const membershipStatusOptions = [
  { key: '', value: 'Select Status' },
  { key: 'user_added', value: 'User Added' },
  { key: 'user_paid', value: 'User Paid' },
  { key: 'member_active', value: 'Member Active' },
  { key: 'tuli_appt_booked', value: 'Tuli Appt Booked' },
  { key: 'tuli_appt_complete', value: 'Tuli Appt Complete' },
  { key: 'sample_received', value: 'Sample Received' },
  { key: 'report_available', value: 'Report Available' },
  { key: 'report_awaiting_review_by_gp', value: 'Report Awaiting Review By GP' },
  { key: 'report_awaiting_discussion', value: 'Report Awaiting Discussion' },
  { key: 'gp_review_booked', value: 'GP Review Booked' },
  { key: 'report_published', value: 'Report Published' },
  { key: 'nutritionist_appt_invite_sent', value: 'Nutritionist Appt Invite Sent' },
  { key: 'nutritionist_appt_booked', value: 'Nutritionist Appt Booked' },
  { key: 'care_notes_uploaded', value: 'Care Notes Uploaded' },
  { key: 'recommendations_uploaded', value: 'Recommendations Uploaded' },
  { key: 'supplements_uploaded', value: 'Supplements Uploaded' },
  { key: 'home_blood_appt_requested', value: 'Home Blood Appt Requested'},
  { key: 'home_blood_appt_booked', value: 'Home Blood Appt Booked'},
  { key: 'home_blood_appt_complete', value: 'Home Blood Appt Complete' },
  { key: 'scan_scheduled', value: 'Scan Scheduled' },
  { key: 'scan_complete', value: 'Scan complete' },
  { key: 'scan_review_invite_sent', value: 'Scan review invite sent' },
  { key: 'mri_scan_published', value: 'MRI Scan published' },
  { key: 'testing_complete_feedback', value: 'Testing complete feedback' },
  { key: 'pre_scan_consult_invite_sent', value: 'Pre-scan consultation invite sent' },
  { key: 'pre_scan_consult_booked', value: 'Pre-scan consultation booked' },
  { key: 'pre_scan_questionnaire_link_sent', value: 'Pre-scan questionnaire link sent' },
  { key: 'pre_scan_questionnaire_submitted', value: 'Pre-scan questionnaire submitted' },
  { key: 'subscription_cancelled', value: 'Subscription Cancelled' }
];

type TypeName = {
  isOpen: boolean;
  onClose: () => void;
  onUpdateList?: () => void;
};

interface FormValues {
  first_name: string;
  last_name: string;
  number: string;
  email: string;
  expDate: Date | null;
  unique_id: string;
  is_paid: boolean;
  dob: Date | null;
  gender: { key: UserGenderEnum | string; value: string };
  membership_type: string;
  membership_status: { key: string; value: string };
  no_onboarding: boolean;
  address: string;
  country: { key: any; value: any };
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  blood_tests_per_year: number | null;
  payment_term: string;
}

const MemberModal: React.FC<TypeName> = ({ isOpen, onClose, onUpdateList }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFormError, setIsFormError] = useState<any>(null);
  const [isError, setIsError] = useState<any>(null);
  const [memberData, setMemberData] = useState<GetMemberResponse | null>(null);
  const [isResendingPayment, setIsResendingPayment] = useState<boolean>(false);
  const { memberIdToEdit } = useSelector((state: RootState) => state.members);
  const prevMembershipTypeRef = useRef<string>('');

  const originalMembershipType = useMemo(() => {
    if (!memberData?.membership_type) return 'Core';
    return memberData.membership_type.charAt(0).toUpperCase() + memberData.membership_type.slice(1).toLowerCase();
  }, [memberData]);

  const validate = (values: FormValues) => {
    const errors: any = {};

    if (!values.first_name) {
      errors.first_name = 'Name is required';
    } else if (values.first_name.length < 2) {
      errors.first_name = 'Name is too short';
    }

    if (!values.last_name) {
      errors.last_name = 'Name is required';
    } else if (values.last_name.length < 2) {
      errors.last_name = 'Name is too short';
    }

    if (!values.email) {
      errors.email = 'Email is required';
    } else if (!emailRegexp.test(values.email)) {
      errors.email = 'Invalid email address';
    }

    if (!values.expDate && values.membership_type !== 'Free') {
      errors.expDate = 'Date is required';
    }

    if (values.no_onboarding && values.membership_type !== 'Free' && !values.dob) {
      errors.dob = 'Date of birth is required when No onboarding is selected';
    }
    if ((values.membership_type === 'Core' || values.membership_type === 'Plus') && values.blood_tests_per_year === null) {
      errors.blood_tests_per_year = 'Number of blood tests is required';
    }

    if ((values.membership_type === 'Core' || values.membership_type === 'Plus') && !values.payment_term) {
      errors.payment_term = 'Payment term is required';
    }
    // if( !values.gender){
    //   errors.gender = 'Gender is required';
    // }

    return errors;
  };

  const handleFieldChange = <T extends keyof FormValues>(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: T
  ) => {
    if (formik.errors[name] && formik.submitCount > 0) {
      formik.setFieldError(name, '');
    }
    formik.handleChange(e);
  };

  const onDateChange = (date: Date | null, key: string) => {
    formik.setFieldValue(key, date);
  };

  const handleAddressChange = (value: string, postalCode?: string) => {
    const addressLine1 = (document.getElementById('address-search-member-modal-line1') as HTMLInputElement)?.value || '';
    const addressLine2 = (document.getElementById('address-search-member-modal-line2') as HTMLInputElement)?.value || '';
    const city = (document.getElementById('address-search-member-modal-city') as HTMLInputElement)?.value || '';
    const postCode = (document.getElementById('address-search-member-modal-postcode') as HTMLInputElement)?.value || postalCode || '';
    const countryName = (document.getElementById('address-search-member-modal-country') as HTMLInputElement)?.value || '';
    
    formik.setFieldValue('address', value);
    formik.setFieldValue('address1', addressLine1);
    formik.setFieldValue('address2', addressLine2);
    formik.setFieldValue('city', city);
    formik.setFieldValue('postalCode', postCode);
    
    if (countryName) {
      const countryOption = countryList().getData().find((c: any) => c.label === countryName);
      if (countryOption) {
        formik.setFieldValue('country', { key: countryOption.label, value: countryOption.label });
      }
    }
  };

  useEffect(() => {
    const fetchMemberData = async () => {
      try {
        setIsLoading(true);
        const resp = await getMember(memberIdToEdit!);
        setMemberData(resp);
      } catch (error) {
        setIsError(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (memberIdToEdit) {
      fetchMemberData();
    } else {
      setMemberData(null);
      setIsError(null);
    }
  }, [memberIdToEdit]);

  const initialValues = useMemo(() => {
    return memberIdToEdit
      ? {
        first_name: memberData?.first_name || '',
        last_name: memberData?.last_name || '',
        email: memberData?.email || '',
        number: memberData?.phone || '',
        expDate: memberData?.subscription_end
          ? new Date(
            memberData.subscription_end.endsWith('Z')
              ? memberData.subscription_end
              : memberData.subscription_end + 'Z'
          )
          : null,
        unique_id: memberData?.unique_id || '',
        is_paid: memberData?.is_paid || false,
        dob: memberData?.dob
          ? new Date(
            memberData.dob.endsWith('Z')
              ? memberData.dob
              : memberData.dob + 'Z'
          )
          : null,
        gender: genders.find(
          (gender) => gender.key === memberData?.gender
        ) || { key: '', value: '' },
        membership_type: memberData?.membership_type
          ? memberData.membership_type.charAt(0).toUpperCase() + memberData.membership_type.slice(1).toLowerCase()
          : 'Core',
        membership_status: membershipStatusOptions.find(
          (status) => status.key === (memberData as any)?.membership_status
        ) || { key: '', value: 'Select Status' },
        no_onboarding: (memberData as any)?.no_onboarding || false,
        address: memberData?.address_line_1
          ? [memberData.address_line_1, memberData.address_line_2, memberData.city, memberData.post_code, memberData.country]
            .filter(Boolean).join(', ')
          : '',
        country: memberData?.country
          ? { key: memberData.country, value: memberData.country }
          : { key: '', value: '' },
        address1: memberData?.address_line_1 || '',
        address2: memberData?.address_line_2 || '',
        city: memberData?.city || '',
        postalCode: memberData?.post_code || '',
        blood_tests_per_year: (memberData as any)?.number_of_blood_test_plan !== undefined && (memberData as any)?.number_of_blood_test_plan !== null
          ? (memberData as any).number_of_blood_test_plan
          : null,
        payment_term: (memberData as any)?.billing_cycle || ''
      }
      : {
        first_name: '',
        last_name: '',
        email: '',
        number: '',
        expDate: null,
        unique_id: '',
        is_paid: false,
        dob: null,
        gender: { key: '', value: '' },
        membership_type: 'Core',
        membership_status: { key: '', value: 'Select Status' },
        no_onboarding: false,
        address: '',
        country: { key: '', value: '' },
        address1: '',
        address2: '',
        city: '',
        postalCode: '',
        blood_tests_per_year: null,
        payment_term: ''
      };
  }, [memberIdToEdit, memberData]);

  const formik = useFormik<FormValues>({
    initialValues,
    validate,
    onSubmit: async (values, { resetForm }) => {
      if (isFormError) setIsFormError(false);
      setIsSubmitting(true);
      try {
        if (
          values.no_onboarding &&
          values.membership_type !== 'Scan' &&
          (!values.address1 || !values.city || !values.postalCode || !values.country?.key)
        ) {
          setIsFormError('Address fields are required when No onboarding is selected');
          setIsSubmitting(false);
          return;
        }
        if (values.no_onboarding && values.membership_type !== 'Free' && !values.dob) {
          setIsFormError('Date of birth is required when No onboarding is selected');
          setIsSubmitting(false);
          return;
        }
        const dobForApi = values?.dob ? values.dob.toLocaleDateString("en-CA") : null;
        const newMemberData = {
          email: values.email,
          phone: values.number ? (values.number[0] !== '+' ? '+' + values.number : values.number) : null,
          first_name: values.first_name,
          last_name: values.last_name,
          ...((values.membership_type !== 'Free') ? { subscription_end: values.expDate ? values.expDate.toISOString() : '' } : {}),
          is_paid: values.is_paid,
          membership_type: values.membership_type.toLowerCase(),
          membership_status: values.membership_status.key || null,
          no_onboarding: values.no_onboarding,
          dob: dobForApi,
          ...((values.membership_type === 'Core' || values.membership_type === 'Plus')
            ? {
                number_of_blood_test_plan: values.blood_tests_per_year || 0,
                billing_cycle: values.payment_term || ''
              }
            : {}),
          ...((values.no_onboarding || memberIdToEdit)
            ? {
                address_line_1: values.address1,
                address_line_2: values.address2,
                city: values.city,
                post_code: values.postalCode,
                country: values.country?.key || '',
              }
            : {}),
          gender:
            genders.find((gender) => gender.key === values?.gender?.key)?.key ||
            null
        };

        const resp = memberIdToEdit
          ? await updateMember({ ...newMemberData, user_id: memberIdToEdit })
          : await createMember(newMemberData);

        if ('detail' in resp && resp?.detail) {
          return setIsFormError(resp?.detail);
        }

        if ('description' in resp && resp?.description) {
          return setIsFormError(resp?.description);
        }

        dispatch(setMemberIdToEdit(null));

        dispatch(setMemberIdToEdit(null));
        onUpdateList && onUpdateList();
        onClose();
        resetForm();
      } catch (error: any) {
        console.error('Error creating/updating member:', error);
        
        let errorMessage = 'Something went wrong. Please try again.';
        
        if (error?.response?.data?.description) {
          errorMessage = error.response.data.description;
        } else if (error?.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        setIsFormError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true
  });

  const { resetForm, setFieldValue, values } = formik;

  useEffect(() => {
    if (isOpen && !memberIdToEdit) {
      resetForm();
      setIsFormError(null);
    }
  }, [isOpen, memberIdToEdit, resetForm]);

  useEffect(() => {
    if (values.membership_type === 'Scan' && !memberIdToEdit) {
      setFieldValue('blood_tests_per_year', null);
      setFieldValue('payment_term', '');
    } else if (values.membership_type === 'Free') {
      setFieldValue('no_onboarding', true);
      setFieldValue('is_paid', true);
    } else if (prevMembershipTypeRef.current === 'Free' && values.membership_type !== 'Free') {
      setFieldValue('no_onboarding', false);
      setFieldValue('is_paid', false);
    }

    if (memberIdToEdit && !values.no_onboarding && prevMembershipTypeRef.current && prevMembershipTypeRef.current !== values.membership_type) {
      if (values.membership_type !== originalMembershipType) {
        setFieldValue('is_paid', false); 
      } else {
        setFieldValue('is_paid', memberData?.is_paid || false);
      }
    }
    prevMembershipTypeRef.current = values.membership_type;
  }, [values.membership_type, values.no_onboarding, memberIdToEdit, originalMembershipType, memberData, setFieldValue]);

  const handleResendPaymentLink = async () => {
    if (!memberIdToEdit) return;
    try {
      setIsResendingPayment(true);
      const response = await resendPaymentLink(memberIdToEdit);
      if ('detail' in response && response.detail) {
        setIsFormError(response.detail);
        return;
      }
      toast.success('Payment link sent successfully');
      dispatch(setMemberIdToEdit(null));
      onClose();
    } catch (error: any) {
      console.error('Error resending payment link:', error);
      setIsFormError('Failed to resend payment link. Please try again.');
    } finally {
      setIsResendingPayment(false);
    }
  };

  const handleClose = () => {
    dispatch(setMemberIdToEdit(null));
    if (formik) {
      formik.resetForm();
    }
    setIsFormError(null);
    setMemberData(null);
    setIsError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={memberIdToEdit ? 'Update member' : 'Add Member'}
      width='520px'
    >
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <div>Error loading member data</div>
      ) : (
        <form
          onSubmit={formik.handleSubmit}
          noValidate
          className='member-modal-container'
        >
          <div className='main-info-container'>
            <div className='section-title'>Membership Type</div>
            <div className='inputs-wrapper'>
              <div className='radio-group'>
                <label>
                  <input
                    type='radio'
                    name='membership_type'
                    checked={formik.values.membership_type === 'Core'}
                    onChange={() => formik.setFieldValue('membership_type', 'Core')}
                  />
                  Core
                </label>
                <label>
                  <input
                    type='radio'
                    name='membership_type'
                    checked={formik.values.membership_type === 'Scan'}
                    onChange={() => formik.setFieldValue('membership_type', 'Scan')}
                  />
                  Scan
                </label>
                <label>
                  <input
                    type='radio'
                    name='membership_type'
                    checked={formik.values.membership_type === 'Plus'}
                    onChange={() => formik.setFieldValue('membership_type', 'Plus')}
                  />
                  Plus
                </label>
                <label>
                  <input
                    type='radio'
                    name='membership_type'
                    checked={formik.values.membership_type === 'Free'}
                    onChange={() => formik.setFieldValue('membership_type', 'Free')}
                  />
                  Free
                </label>
              </div>
            </div>
          </div>
          <div className='main-info-container'>
            <div className='section-title'>Main Info</div>
            <div className='inputs-wrapper'>
              <TextInput
                name='name'
                placeholder='Enter name *'
                type='text'
                isError={formik.submitCount > 0 && !!formik.errors.first_name}
                errorMessage={
                  formik.submitCount > 0 ? formik.errors.first_name : undefined
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  formik.setFieldValue('first_name', newValue);
                  if (formik.errors.first_name && formik.submitCount > 0) {
                    if (newValue.length >= 2 || newValue.length === 0) {
                      formik.setFieldError('first_name', '');
                    } else {
                      formik.validateField('first_name');
                    }
                  }
                }}
                value={formik.values.first_name}
              />
              <TextInput
                name='name'
                placeholder='Enter last name *'
                type='text'
                isError={formik.submitCount > 0 && !!formik.errors.last_name}
                errorMessage={
                  formik.submitCount > 0 ? formik.errors.last_name : undefined
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  formik.setFieldValue('last_name', newValue);
                  if (formik.errors.last_name && formik.submitCount > 0) {
                    if (newValue.length >= 2 || newValue.length === 0) {
                      formik.setFieldError('last_name', '');
                    } else {
                      formik.validateField('last_name');
                    }
                  }
                }}
                value={formik.values.last_name}
              />
            </div>
          </div>
         
          <div className='main-info-container'>
            <div className='section-title'>Gender</div>
            <Select
              name='gender'
              selectedOption={formik.values.gender}
              options={genders}
              onChange={(option) => formik.setFieldValue('gender', option)}
              isError={false}
              placeholder='Enter the gender '
            />
          </div>
          <div className='main-info-container'>
            <div className='section-title'>Contact Info</div>
            <div className='inputs-wrapper'>
              <TextInput
                name='email'
                placeholder='Enter email *'
                type='text'
                isError={formik.submitCount > 0 && !!formik.errors.email}
                errorMessage={
                  formik.submitCount > 0 ? formik.errors.email : undefined
                }
                onChange={handleFieldChange}
                value={formik.values.email}
              />
              <PhoneInputComp
                placeholder={formik.values.membership_type === 'Free' ? 'Enter number' : 'Enter number *'}
                selectedPhone={formik.values.number}
                onPhoneChange={(phone) => formik.setFieldValue('number', phone)}
              />
            </div>
          </div>

          {formik.values.membership_type !== 'Free' && (
            <div className='main-info-container'>
              <div className='section-title'>Expiration Date</div>
              <div className='inputs-wrapper'>
                <DatePicker
                  onDateChange={(val) => onDateChange(val, 'expDate')}
                  placeholder='Enter date *'
                  selectedDate={formik.values.expDate}
                />
              </div>
            </div>
          )}

          <div className='main-info-container'>
            <div className='section-title'>Date of birth{formik.values.no_onboarding && formik.values.membership_type !== 'Free' ? ' *' : ''}</div>
            <div className='inputs-wrapper'>
              <DatePicker
                onDateChange={(val) => {
                  onDateChange(val, 'dob');
                  if (formik.errors.dob && formik.submitCount > 0) {
                    formik.setFieldError('dob', '');
                  }
                }}
                showYearDropdown
                placeholder={formik.values.no_onboarding && formik.values.membership_type !== 'Free' ? 'Enter date *' : 'Enter date'}
                selectedDate={formik.values.dob}
                subMaxDateDays={5840}//16 year age minimum
              />
              {formik.submitCount > 0 && formik.errors.dob && (
                <div className='error-message' style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                  {formik.errors.dob}
                </div>
              )}
            </div>
          </div>
          
          <div className='main-info-container'>
            <div className='inputs-wrapper'>
              <label>
                <input
                  type='checkbox'
                  checked={formik.values.no_onboarding}
                  disabled={formik.values.membership_type === 'Free'}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    formik.setFieldValue('no_onboarding', isChecked);
                    if (isChecked) {
                      formik.setFieldValue('is_paid', true);
                    } else {
                      if (memberIdToEdit) {
                        if (formik.values.membership_type !== originalMembershipType) {
                          formik.setFieldValue('is_paid', false);
                        } else {
                          formik.setFieldValue('is_paid', memberData?.is_paid || false);
                        }
                      } else {
                        formik.setFieldValue('is_paid', false);
                      }
                      // Clear dob error if unchecking no_onboarding
                      if (formik.errors.dob) {
                        formik.setFieldError('dob', '');
                      }
                    }
                  }}
                />
                {' '}No onboarding
              </label>
            </div>
            {(memberIdToEdit || formik.values.no_onboarding) && (
              <div className='select-address-container'>
                <div className='address-search-wrapper input-wrapper'>
                  <div className='text-input-title '>
                    Address{formik.values.no_onboarding && formik.values.membership_type !== 'Scan' ? ' *' : ''}
                  </div>
                  <CustomInput
                    value={formik.values.address}
                    onChange={handleAddressChange}
                    id="address-search-member-modal"
                    placeholder='Start typing your address or postcode'
                  />
                </div>
                <div className='text-input-title '>Address Line 2</div>
                <TextInput
                  name='address2'
                  placeholder='Enter Address Line 2'
                  isError={false}
                  onChange={(e) => formik.setFieldValue('address2', e.target.value)}
                  value={formik.values.address2}
                />
                <div className='text-input-title '>
                  City{formik.values.no_onboarding && formik.values.membership_type !== 'Scan' ? ' *' : ''}
                </div>
                <TextInput
                  name='city'
                  placeholder={
                    formik.values.no_onboarding && formik.values.membership_type !== 'Scan'
                      ? 'Enter City *'
                      : 'Enter City'
                  }
                  isError={false}
                  onChange={(e) => formik.setFieldValue('city', e.target.value)}
                  value={formik.values.city}
                />
                <div className='text-input-title '>
                  Post Code{formik.values.no_onboarding && formik.values.membership_type !== 'Scan' ? ' *' : ''}
                </div>
                <TextInput
                  name='postalCode'
                  placeholder={
                    formik.values.no_onboarding && formik.values.membership_type !== 'Scan'
                      ? 'Enter Post Code *'
                      : 'Enter Post Code'
                  }
                  isError={false}
                  onChange={(e) => formik.setFieldValue('postalCode', e.target.value)}
                  value={formik.values.postalCode}
                />
                <div className='text-input-title '>
                  Country{formik.values.no_onboarding && formik.values.membership_type !== 'Scan' ? ' *' : ''}
                </div>
                <Select
                  name='country'
                  placeholder='Select country'
                  options={countryList().getData().map((c: any) => ({ key: c.label, value: c.label }))}
                  selectedOption={formik.values.country}
                  onChange={(option) => formik.setFieldValue('country', option)}
                  isError={false}
                />
              </div>
            )}
          </div>
          {formik.values.membership_type !== 'Free' && (
            <div className='main-info-container'>
              <div className='section-title'>Collect payment</div>
              <div className='inputs-wrapper'>
                <div className='radio-group'>
                  <label>
                    <input
                      type='radio'
                      name='is_paid'
                      checked={formik.values.is_paid === false}
                      onChange={() => formik.setFieldValue('is_paid', false)}
                      disabled={formik.values.no_onboarding}
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type='radio'
                      name='is_paid'
                      checked={formik.values.is_paid === true}
                      onChange={() => formik.setFieldValue('is_paid', true)}
                      disabled={formik.values.no_onboarding}
                    />
                    No
                  </label>
                </div>
                {memberIdToEdit && !memberData?.is_paid && (
                  <Button
                    text='Resend Payment Link'
                    onClick={handleResendPaymentLink}
                    isLoading={isResendingPayment}
                    type='button'
                    variant='secondary'
                  />
                )}
              </div>
            </div>
          )}
          {(formik.values.membership_type === 'Core' || formik.values.membership_type === 'Plus') && (
            <>
              <div className='main-info-container'>
                <div className='section-title'>Number of blood tests *</div>
                <div className='inputs-wrapper'>
                  <div className='radio-group'>
                    <label>
                      <input
                        type='radio'
                        name='blood_tests_per_year'
                        checked={formik.values.blood_tests_per_year === 1}
                        onChange={() => formik.setFieldValue('blood_tests_per_year', 1)}
                      />
                      1
                    </label>
                    <label>
                      <input
                        type='radio'
                        name='blood_tests_per_year'
                        checked={formik.values.blood_tests_per_year === 2}
                        onChange={() => formik.setFieldValue('blood_tests_per_year', 2)}
                      />
                      2
                    </label>
                    <label>
                      <input
                        type='radio'
                        name='blood_tests_per_year'
                        checked={formik.values.blood_tests_per_year === 3}
                        onChange={() => formik.setFieldValue('blood_tests_per_year', 3)}
                      />
                      3
                    </label>
                    <label>
                      <input
                        type='radio'
                        name='blood_tests_per_year'
                        checked={formik.values.blood_tests_per_year === 4}
                        onChange={() => formik.setFieldValue('blood_tests_per_year', 4)}
                      />
                      4
                    </label>
                  </div>
                </div>
              </div>
              <div className='main-info-container'>
                <div className='section-title'>Payment term *</div>
                <div className='inputs-wrapper'>
                  <div className='radio-group'>
                    <label>
                      <input
                        type='radio'
                        name='payment_term'
                        checked={formik.values.payment_term === 'monthly'}
                        onChange={() => formik.setFieldValue('payment_term', 'monthly')}
                      />
                      Monthly
                    </label>
                    <label>
                      <input
                        type='radio'
                        name='payment_term'
                        checked={formik.values.payment_term === 'yearly'}
                        onChange={() => formik.setFieldValue('payment_term', 'yearly')}
                      />
                      Yearly
                    </label>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className='main-info-container'>
            <div className='section-title'>Membership Status</div>
            <div className='inputs-wrapper'>
              <Select
                name='membership_status'
                selectedOption={formik.values.membership_status}
                options={membershipStatusOptions}
                onChange={(option) => formik.setFieldValue('membership_status', option)}
                isError={false}
              />
            </div>
          </div>
          <div className='btn-wrapper'>
            <Button
              text={memberIdToEdit ? 'Update member' : 'Add Member'}
              type='submit'
              isLoading={isSubmitting}
              isDisabled={
                !!(
                  !formik.values.last_name ||
                  !formik.values.first_name ||
                  !formik.values.email ||
                  (!formik.values.number && formik.values.membership_type !== 'Free') ||
                  (!formik.values.expDate && formik.values.membership_type !== 'Free') ||
                  (formik.values.no_onboarding &&
                    ((!formik.values.dob && formik.values.membership_type !== 'Free') ||
                      (formik.values.membership_type !== 'Scan' &&
                        (!formik.values.address1 ||
                          !formik.values.city ||
                          !formik.values.postalCode ||
                          !formik.values.country?.key)))) ||
                  ((formik.values.membership_type === 'Core' || formik.values.membership_type === 'Plus') && formik.values.blood_tests_per_year === null) ||
                  ((formik.values.membership_type === 'Core' || formik.values.membership_type === 'Plus') && !formik.values.payment_term) ||
                  formik.errors.last_name ||
                  formik.errors.first_name ||
                  formik.errors.email ||
                  formik.errors.number ||
                  formik.errors.expDate ||
                  formik.errors.dob
                )
              }
            />

            {isFormError && (
              <div className='error-message'>
                {typeof isFormError === 'string'
                  ? isFormError
                  : 'Something went wrong. Please try again.'}
              </div>
            )}
          </div>
          <div>
            <div className='section-title'>Member ID</div>
            {formik.values.unique_id}
          </div>
        </form>
      )}
    </Modal>
  );
};

export default MemberModal;
